// Considered: ScopeCoinAtom — rejected because it is a marketplace-specific coin shape;
// GlyphAtom IS the convergence primitive that ScopeCoinAtom will delegate to. See spec Reuse Audit.

import React from 'react';
import { Icon, addCollection, getIcon } from '@iconify/react';
import lucideData from '@iconify-json/lucide/icons.json';
import { isValidGlyphColor } from '@center-platform/spheres/glyph';
import type { GlyphColor, GlyphKind } from '../../../types/glyph';
import { resolveGlyph } from './resolveGlyph';
import { normalizeLegacyGlyph } from './legacyGlyphMap';
import './GlyphAtom.scss';

// Register Lucide collection at module load — offline, no CDN required
addCollection(lucideData as never);

export interface GlyphAtomProps {
  /** Icon identifier: 'lucide:building', a legacy schema-avatar CSS class (normalized to Iconify), or letter text */
  glyph?: string | null;
  /** Override auto-detection via resolveGlyph */
  glyphKind?: GlyphKind;
  /** Token reference or hue override for icon color */
  glyphColor?: GlyphColor;
  /** Text to extract fallback initial from when glyph is absent */
  fallbackText?: string;
  /**
   * Iconify name to render when `glyph` is unset (null/undefined/empty) — the
   * sphere type editor's rendered prototype never falls back to a letter for
   * "no glyph chosen yet" (`type-rail/trail-composer.jsx:884-886`: when its own
   * glyph-mark component isn't available it renders a fixed "database" icon,
   * never a letter). `fallbackText` keeps producing the letter for consumers
   * that want an initials avatar; this prop is additive so every existing
   * consumer is unaffected.
   */
  emptyIcon?: string;
  /**
   * `mark` (default) is the bare glyph, sized by the surrounding font. `tile` seats it in a
   * fixed square tinted from its own colour — the treatment a glyph gets when it stands for
   * a whole thing (a type in its header, an aspect on its row, a parent on its card) rather
   * than annotating a line of text. Without it every `data-kind="tile"` datum was a bare
   * letter floating beside a title.
   */
  variant?: 'mark' | 'tile';
  /** Tile side. `md` is the header tile; `sm` is the row tile. Ignored for `mark`. */
  tileSize?: 'sm' | 'md';
  /**
   * `auto` (default) tints the tile from the glyph's own colour when one is set, else
   * falls to the neutral gradient default — both read the resolved `--glyph-color`.
   * `fixed` is a third, uncoloured treatment: a bordered, light-surface tile with a
   * single primary-hue mark, the SAME for every instance regardless of that instance's
   * own glyph colour (`studio.css:544`, `.t-asp-glyph` — the aspect row's glyph is a
   * fixed look, not per-aspect tinted). Ignored for `variant="mark"`.
   */
  tileTreatment?: 'auto' | 'fixed';
  className?: string;
  'aria-label'?: string;
  /**
   * Marks a purely decorative glyph so assistive tech skips it. Without this, the letter
   * fallback ("R") is announced alongside the label the glyph already sits next to. An empty
   * `aria-label` does NOT achieve this — AT ignores it and announces the text content.
   */
  'aria-hidden'?: boolean;
  /** Self-description for the parity walker — see TagAtom/StatusBadgeAtom. */
  'data-datum'?: string;
  'data-kind'?: string;
  /**
   * When the contract addresses the tile and the glyph inside it as two datums (the
   * header's `header.tile` > `header.glyph`), this names the tile; `data-datum` then
   * names the mark, which moves onto an inner span. Only meaningful with `variant="tile"`.
   */
  'data-tile-datum'?: string;
  /**
   * Contract datum for the LETTER inside the glyph, when the contract addresses it
   * as its own leaf (the aspect row's `compose.g-text`). Distinct from `data-datum`,
   * which addresses the tile; an icon-rendered glyph has no letter and emits nothing.
   */
  'data-letter-datum'?: string;
  /**
   * `data-kind` for the letter leaf named by `data-letter-datum`. Defaults to `strong`
   * (the aspect row's `compose.g-text`); the identity/placement glyph picker's tile
   * addresses the same letter as `text` (`ident.glyph-text`), so it is a prop rather
   * than baked in.
   */
  'data-letter-kind'?: string;
  'data-testid'?: string;
}

function getInitial(text: string): string {
  const first = [...text.trim()][0];
  return first ? first.toUpperCase() : '?';
}

const GlyphAtom: React.FC<GlyphAtomProps> = ({
  glyph,
  glyphKind,
  glyphColor,
  fallbackText,
  emptyIcon,
  variant = 'mark',
  tileSize = 'md',
  tileTreatment = 'auto',
  className = '',
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden,
  'data-datum': dataDatum,
  'data-kind': dataKind,
  'data-tile-datum': dataTileDatum,
  'data-letter-datum': dataLetterDatum,
  'data-letter-kind': dataLetterKind = 'strong',
  'data-testid': dataTestId,
}) => {
  // Normalize legacy schema-avatar CSS classes (e.g. 'center-organization-icon')
  // to Iconify names so values from un-backfilled runtime sources still render.
  const normalizedGlyph = normalizeLegacyGlyph(glyph);
  const resolvedKind: GlyphKind = glyphKind ?? resolveGlyph(normalizedGlyph);

  const isFixedTile = variant === 'tile' && tileTreatment === 'fixed';

  const style: React.CSSProperties & Record<string, string> = {};
  // `fixed` never tints — every instance renders the same primary-hue mark
  // regardless of that instance's own glyph colour.
  // Stored colours are user data; only the closed forms reach a style value.
  const safeColor = isValidGlyphColor(glyphColor) ? glyphColor : undefined;
  if (isFixedTile) {
    // no-op
  } else if (safeColor?.kind === 'token') {
    // `--glyph-token-*` is the defined, theme-adaptive palette (themes/common.scss).
    // The two fallbacks keep older stored token names working (`scope-organization`)
    // and, failing that, render readable text colour — never the black an
    // undefined custom property resolves to.
    style['--glyph-color'] = `var(--glyph-token-${safeColor.value}, var(--${safeColor.value}, var(--text-color)))`;
  } else if (safeColor?.kind === 'hex') {
    style['--glyph-color'] = safeColor.value;
  } else if (safeColor?.kind === 'hue') {
    style['--glyph-hue'] = String(safeColor.value);
  }

  const hasCustomColor = Object.keys(style).length > 0;

  const classes = [
    'glyph-atom',
    !isFixedTile && glyphColor?.kind === 'hue' ? 'glyph-atom--hue' : '',
    variant === 'tile' ? `glyph-atom--tile glyph-atom--tile-${tileSize}` : '',
    // `studio.css:464-465` (`.t-glyph` / `.t-glyph[data-tinted]`): untinted is a
    // neutral gradient tile with a white mark; only a chosen glyph colour washes
    // the tile in its own hue. `fixed` (`studio.css:544`, `.t-asp-glyph`) skips this
    // split entirely — it never tints, so it is checked first.
    variant === 'tile'
      ? (isFixedTile ? 'glyph-atom--tile-fixed' : hasCustomColor ? 'glyph-atom--tile-tinted' : 'glyph-atom--tile-default')
      : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  let content: React.ReactNode;

  if (resolvedKind === 'iconify') {
    // Only render icons present in a registered (offline-bundled) collection.
    // An unbundled name would make @iconify/react fetch from the Iconify CDN;
    // gating on getIcon keeps rendering fully offline, falling back to a letter.
    content = normalizedGlyph && getIcon(normalizedGlyph)
      ? <Icon icon={normalizedGlyph} className="glyph-atom__icon" />
      : (
        <span
          className="glyph-atom__letter"
          data-datum={dataLetterDatum}
          data-kind={dataLetterDatum ? dataLetterKind : undefined}
        >
          {fallbackText ? getInitial(fallbackText) : '?'}
        </span>
      );
  } else if (!glyph && emptyIcon && getIcon(emptyIcon)) {
    content = <Icon icon={emptyIcon} className="glyph-atom__icon" />;
  } else {
    const letter = glyph
      ? glyph[0]?.toUpperCase() ?? '?'
      : fallbackText
        ? getInitial(fallbackText)
        : '?';
    content = (
      <span
        className="glyph-atom__letter"
        data-datum={dataLetterDatum}
        data-kind={dataLetterDatum ? dataLetterKind : undefined}
      >
        {letter}
      </span>
    );
  }

  const splitDatums = variant === 'tile' && Boolean(dataTileDatum);

  return (
    <span
      className={classes}
      style={hasCustomColor ? style : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaHidden}
      data-datum={splitDatums ? dataTileDatum : dataDatum}
      data-kind={splitDatums ? 'tile' : dataKind}
      data-testid={dataTestId}
    >
      {splitDatums ? (
        <span className="glyph-atom__mark" data-datum={dataDatum} data-kind={dataKind}>
          {content}
        </span>
      ) : content}
    </span>
  );
};

GlyphAtom.displayName = 'GlyphAtom';

export default GlyphAtom;
